/***
 * Created on 2025/4/30
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {useReducer, useRef, useState} from 'react';
import ModalComponent from "../common/ModalComonent/ModalComponent";
import {useTranslation} from "react-i18next";
import TableComponent from "../common/TableComponent/TableComponent";
import UserAvatarIcon from "../common/UserAvatarIcon";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useOrganization} from "../../context/OrganizationContext";
import OrgContentPlaceholderComponent from "../organization-content/OrgContentPlaceholderComponent";
import {normalizeSpaceSize} from "../../service/utils";
import {invalidateAccessGrant} from "../../service/accessGrantSync";
import {useAlertContext} from "../../context/AlertContext";

const table_ui = {
    table_field_checkbox: true
}

export const computeSelectBoxState = (data = []) => {
    if (!data.length) {
        return false;
    }
    let check_status = data?.every((item) => item.checked);
    if (!check_status) {
        check_status = data?.some((item) => item.checked) ? 'partial' : false;
    }
    return check_status;
}

export const getSelectedSeatCount = (data = []) => data.filter((item) => item.checked).length;

export const isOverCapacity = (data = [], totalSeats = 0) => getSelectedSeatCount(data) > totalSeats;

export const getRemainingSeats = (data = [], totalSeats = 0) => {
    return Math.max(totalSeats - getSelectedSeatCount(data), 0);
}

export const getSelectableRowsForBulkSelect = (data = [], remainingSeats = 0) => {
    if (remainingSeats <= 0) {
        return [];
    }
    return data
        .filter((item) => !item.checked)
        .slice(0, remainingSeats)
        .map((item) => item.id);
}

export const shouldDisableSelectAll = ({selectedSeatCount = 0, totalSeats = 0, selectBox = false}) => {
    return selectedSeatCount >= totalSeats && selectBox === false;
}

export const canSubmitSeatAssignments = ({data = [], totalSeats = 0}) => {
    const hasTouchedRows = data.some((item) => item?.touched);
    return hasTouchedRows && !isOverCapacity(data, totalSeats);
}

export const reducerHandler = (state, action) => {
    switch (action.type) {
        case 'update_data':
            const seeded = action.payload.data.map((item) => ({
                ...item,
                initial_checked: !!item.checked,
                touched: false,
            }));
            return {
                ...state,
                data: seeded,
                select_box: computeSelectBoxState(seeded)
            }
        case 'select_all':
            const remainingSeats = getRemainingSeats(state.data, state?.region_data?.seats?.total || 0);
            const selectableIds = getSelectableRowsForBulkSelect(state.data, remainingSeats);
            if (!selectableIds.length) {
                const cleared = state.data.map((item) => {
                    if (item.checked) {
                        return {...item, checked: false, touched: false !== item.initial_checked};
                    }
                    return item;
                });
                return {
                    ...state,
                    data: cleared,
                    select_box: false,
                    input: state.input + 1
                };
            }
            const selected = state.data.map((item) => {
                if (selectableIds.includes(item.id)) {
                    return {...item, checked: true, touched: true !== item.initial_checked};
                }
                return item;
            });
            return {
                ...state,
                data: selected,
                select_box: computeSelectBoxState(selected),
                input: state.input + 1
            }
        case 'select_none':
            const unSelected = state.data.map((item) => {
                if (item.checked) {
                    return {...item, checked: false, touched: false !== item.initial_checked};
                }
                return item;
            });
            return {
                ...state,
                data: unSelected,
                select_box: false,
                input: state.input + 1
            }
        case 'toggle_checkbox':
            const updatedData = state.data.map((item) => {
                if (item.id === action.payload.id) {
                    const nextChecked = !item.checked;
                    return {...item, checked: nextChecked, touched: nextChecked !== item.initial_checked};
                }
                return item;
            });
            return {
                ...state,
                data: updatedData,
                select_box: computeSelectBoxState(updatedData),
                input: state.input + 1
            }
        default:
            return state;
    }
}

function CloudSpaceEmptyList({t}) {
    return (
        <div className="flexbox-tbody">
            <div className="no-data-screen">
                <div className="no-data-info">
                    <div className="no-data-desc">
                        <div className="desc-title">{t('@No available users@')}</div>
                        {/*<div className="desc-msg">{t('@You can designate members who will have unrestricted storage space through the Add button above.@')}</div>*/}
                    </div>
                </div>
            </div>
        </div>
    );
}

function CloudSpaceAssignUserStorageRowComponent({row_data, index, doRefresh, disPatcher, translate:t, isOwner, permissions=null, show_checkbox=false, table_head_fields, state}) {
    const toggle_checkbox = (row) => {
        if (state?.data?.filter(r=>r.checked)?.length>=state?.region_data?.seats?.total && !row_data?.checked) return;
        disPatcher({type:'toggle_checkbox', payload: row});
    }

    const usage = row_data?.quota_usages?.filter((o) => o.region === state.region_data.region)?.reduce((total, u) => total + u.usage, 0) || null;

    return (
        <div className="flexbox-trow" key={row_data?.id}>
            <div className="tbody-cell select-cell">
                <div className="comp-checkbox no-label">
                    <input type="checkbox"
                           id={row_data?.id}
                           checked={row_data?.checked || false}
                            disabled={state?.data?.filter(r=>r.checked)?.length>=state?.region_data?.seats?.total && !row_data?.checked}
                           onChange={()=>toggle_checkbox(row_data)}/>
                    <label htmlFor={row_data?.id} className="comp-label"></label>
                </div>
            </div>
            <div className={`tbody-cell ${table_head_fields[0]?.width_style}`}>
                <div className="admin-info">
                    <UserAvatarIcon user_info={row_data} with_user_info={true} avatar_class='admin'/>
                </div>
            </div>
            <div className={`tbody-cell ${table_head_fields[1]?.width_style}`}>{usage ? normalizeSpaceSize(usage) : (row_data?.seated_regions ? normalizeSpaceSize(0) : '-')}</div>
        </div>
    );
}

function CloudSpaceAssignUserStorageModal({openModal, closeModal, region_data}) {
    const {t} = useTranslation();
    const modal = useRef(null);
    const {OrganizationAPI} = useOrganizationApi();
    const {getCurrentOrg} = useOrganization();
    const org = getCurrentOrg();
    const currentUser = JSON.parse(window.localStorage.getItem('currentUser'));
    const queryClient = useQueryClient();
    const alert = useAlertContext();
    const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

    const table_head_fields = [
        {field_name: t('@Member@'), isSortable: true, width_style: '!w-50', field_id: 'display_name', default_sort: true, default_sort_order: ''},
        {field_name: t('@Total Usage@'), isSortable: false, width_style: '!w-50', info:{show_info: true, info_text: t('@Total usage across Personal, Shared, and Global Spaces in this region.@')}},
    ];

    const {data, isFetched} = useQuery({
        queryKey: ['unrestricted_storage_users', region_data?.region],
        queryFn: async (data) => {
            const resp = await OrganizationAPI.q2.get(`org_storage/${org.organization_id}/user?region=${region_data?.region}`);
            const orgUsers = org?.users || [];
            const result = resp.result.filter(u => u.in_org).map((item) => {
                const user = orgUsers.find((u) => u.id === item.user_id);
                return {
                    ...item,
                    status: user.status,
                    id: item.user_id,
                    checked: !!item?.seated_regions?.[region_data?.region]
                }
            });
            dispatch({type: 'update_data', payload: {data: result.filter(u => u.status === 'active')}});
            return result;
        },
    });

    const reducerInitialize = () => {
        return {
            data: data || [],
            region_data,
            current_seated: region_data?.seats?.used || 0,
            select_box: false,
            input: 1
        }
    }

    const [state, dispatch] = useReducer(reducerHandler, {}, reducerInitialize);
    const totalSeats = region_data?.seats?.total || 0;
    const selectedSeatCount = getSelectedSeatCount(state?.data || []);
    const disableSelectAll = shouldDisableSelectAll({
        selectedSeatCount,
        totalSeats,
        selectBox: state?.select_box || false,
    });

    const preSubmit = (input) => {
        return canSubmitSeatAssignments({
            data: state.data,
            totalSeats,
        });
    }

    const assignMutation = useMutation({
        mutationFn: async  (data) => await OrganizationAPI.q2.patch(`org_storage/${org.organization_id}/user`, null, data),
        onSuccess: async (res,variables) => {
            setShowRevokeConfirm(false);
            let meta_data = {add: [], delete: []};
            for (const action of variables) {
                if (action.op === 'add') {
                    meta_data.add.push(action);
                }
                else if (action.op === 'delete') {
                    meta_data.delete.push(action);
                }
            }
            const activity_data = {
                organization_id: org.organization_id,
                user_id: currentUser.user_id,
                category: 6
            }
            if (meta_data.add.length>0) {
                await OrganizationAPI.put('organization_activity',null, {...activity_data, event: 22, meta_data: {data: meta_data.add}});
            }
            if (meta_data.delete.length>0) {
                await OrganizationAPI.put('organization_activity',null, {...activity_data, event: 23, meta_data: {data: meta_data.delete}});
            }
            invalidateAccessGrant(queryClient, org.organization_id);
            closeModal();
            alert(true, 'success', 0, t('@Seat assigned successfully.@'), 10000);
        },
        onError: (err) => {
            console.log(err);
            const nestedCode = err?.response?.data?.errors?.[0]?.code;
            const code = nestedCode || err?.response?.data?.code;
            alert(true, 'error', code, 'Failed to assign seat. Please check your available seat count and try again.');
        }
    });

    const onSubmit = async () => {
        if (isOverCapacity(state.data, totalSeats)) {
            alert(true, 'error', 0, t('@Selected seats exceed this region\'s seat limit. Please deselect members before applying.@'));
            return;
        }
        if (!state.data.some(u => u.checked && u.is_active_org_owner)) {
            alert(true, 'error', 'org_owner_should_be_added_first', t('@Assignment blocked: at least one Organization Owner must hold a storage seat. Add an Owner to the selection to continue.@'));
            return;
        }
        let has_delete_op = false;
        const data = state.data.filter((o) => o?.touched).sort((a,b)=>{
            if (a.is_active_org_owner && !b.is_active_org_owner) return -1;
            if (!a.is_active_org_owner && b.is_active_org_owner) return 1;
            return 0;
        }).map(o => {
            const op= o.checked ? 'add' : 'delete';
            if (op === 'delete') {
                has_delete_op = true;
            }
            return {
                op,
                user_id: o.id,
                qid: o.email,
                region: region_data.region
            }
        });
        if (!showRevokeConfirm && has_delete_op) {
            setShowRevokeConfirm(true);
            modal.current.setSubmitText(t('@Confirm@'));
        }
        else {
            await assignMutation.mutateAsync(data);
        }
    }

    const onClose = () => {
        if (showRevokeConfirm) {
            setShowRevokeConfirm(false);
        }
        else {
            closeModal();
        }
    }

    return (
        <ModalComponent isOpen={openModal} onClose={onClose}
                        onSubmit={onSubmit}
                        title={showRevokeConfirm ? t('@{{revoke_count}} Members Will Lose Access@', {revoke_count: state.data?.filter(o=>o.touched && !o.checked)?.length}) : t('@Manage User Storage Seats@')}
                        modalSize={showRevokeConfirm ? 'medium' : 'large'}
                        okText={t('@Apply@')} ref={modal} preSubmit={preSubmit} input={state.input}>
            <div className="dialog-content">
                {showRevokeConfirm &&
                    <>
                        <div className="text-wrap">
                            {t('@You are about to revoke storage seats for the selected member(s).@')}
                        </div>
                        <div className="amount-info fs14 mb-16">
                            {t('@Their personal spaces will no longer be accessible to them. These personal spaces will be converted into Shared Spaces, allowing owners to continue managing the files.@')}
                        </div>
                        <div className="text-wrap">
                            {t('@Do you want to proceed?@')}
                        </div>

                    </>
                }
                {!showRevokeConfirm &&
                    <>
                        <div className="text-wrap">
                            {t('@Toggle the switch to assign or revoke storage seats for members in {{region}}.@', {region: t(`@${region_data?.region}@`)})}
                        </div>
                        <div className="amount-info fs14 mb-16">
                            {t('@Assigned seats:@')}
                            <span className="fw5 hi-light !pl-8">{selectedSeatCount} / {region_data?.seats?.total || 0}</span>
                        </div>

                         {!isFetched && <OrgContentPlaceholderComponent show_partial={true}/>}

                        {isFetched &&
                            <TableComponent
                                table_head_fields={table_head_fields}
                                table_data={state}
                                state={state}
                                disPatcher={dispatch}
                                search={false}
                                row_component={CloudSpaceAssignUserStorageRowComponent}
                                table_ui={{...table_ui, table_field_checkbox_disabled: disableSelectAll}}
                                // css_with_context_wrapper={false}
                                data_table_name='assign-user-storage-table'
                                empty_data={<CloudSpaceEmptyList t={t}/>}
                                pagination={false}
                                table_height={'335px'}
                            />}
                    </>
                }
            </div>
        </ModalComponent>
    );
}

export default CloudSpaceAssignUserStorageModal;
